import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  authInitializedState,
  currentUserAuthTypeState,
  currentUserIDState,
  isMasterState,
  isMemberState,
} from '@atom/setting/user';
import { cookieStore } from '@store/cookie/cookieStore';
import { AUTH_TYPE, AUTH_TYPE_VALUE } from '@typings/common/authorizationType';
import { useRecoilValue } from 'recoil';
import { ACCESS_TYPE, ACCESS_TYPE_VALUE } from '@typings/common/accessType';
import { useCollection } from '@nandorojo/swr-firestore';
import {
  IMemberRole,
  MEMBER_ROLE_COLLECTION,
} from '@typings/setting/memberRole.interface';

type readProps = {
  accessType: ACCESS_TYPE_VALUE;
  authorUid: string;
  parentAuthorUid?: string;
  originalBoardAuthorUid?: string;
  anonymousPassword?: string;
  /** 비로그인 익명 유저가 자신의 원글에 달린 비밀 댓글을 열람할 수 있도록
   *  원글(부모 게시글)의 anonymousPassword를 전달합니다. */
  parentAnonymousPassword?: string;
  docId?: string;
  isPassowrdMatch?: boolean;
  allowAnonymousEdit?: boolean;
};

export function useReadPermission({
  accessType,
  authorUid,
  parentAuthorUid,
  originalBoardAuthorUid,
  anonymousPassword,
  parentAnonymousPassword,
  docId,
  isPassowrdMatch,
  allowAnonymousEdit,
}: readProps): boolean {
  // 기본값으로 읽기 권한을 허용하지 않음
  const [canRead, setCanRead] = useState<boolean>(false);
  // NOTE: Firebase Auth 의 동기 참조(`fuego.auth().currentUser?.uid`) 는 onAuthStateChanged
  // 첫 콜백 이전 렌더에서 undefined 로 굳어 마스터 권한이 오판되는 레이스를 유발했다.
  // Recoil 의 currentUserIDState 로 SSOT 통일.
  const currentUserID = useRecoilValue(currentUserIDState);
  const isMaster = useRecoilValue(isMasterState);
  const isMember = useRecoilValue(isMemberState);
  // 익명 비밀글/댓글 열람 경로는 쿠키 기반이므로 유지.
  const cashAnonymousPassword = cookieStore.getAnonymousPassword();

  useLayoutEffect(() => {
    const checkPermission = () => {
      switch (accessType) {
        case ACCESS_TYPE.PUBLIC:
          return allowAnonymousEdit || true;
        case ACCESS_TYPE.GUEST:
          // 손님 권한: 멤버가 아닌 사용자(비로그인/게스트) + 관리자
          return isMaster || !isMember;
        case ACCESS_TYPE.SECRET:
          if (!currentUserID) {
            // 이 댓글/글 자체를 익명으로 작성한 사람이라면 열람 허용
            if (anonymousPassword && anonymousPassword === cashAnonymousPassword) {
              return true;
            }
            // 비로그인 익명 유저가 자신의 원글에 달린 비밀 댓글을 읽는 경우:
            // 원글의 anonymousPassword와 현재 쿠키가 일치하면 원글 작성자 본인
            if (
              parentAnonymousPassword &&
              cashAnonymousPassword &&
              parentAnonymousPassword === cashAnonymousPassword
            ) {
              return true;
            }
            return isPassowrdMatch || false;
          } else {
            // 로그인한 멤버 이상이 비밀글을 열람할 때
            if (
              isMaster ||
              authorUid === currentUserID ||
              allowAnonymousEdit ||
              isPassowrdMatch ||
              parentAuthorUid === currentUserID ||
              originalBoardAuthorUid === currentUserID
            ) {
              // 작성자 본인이거나 상위 보드의 작성자라면 열람 가능

              return true;
            } else {
              // master가 아니라면 비공개 표시
              return isMaster;
            }
          }

        case ACCESS_TYPE.MEMBER:
          return (
            isMaster ||
            (currentUserID && (isMember || authorUid === currentUserID))
          );
        case ACCESS_TYPE.PASSWORD:
          return isMaster || isPassowrdMatch;
        default:
          return false;
      }
    };

    setCanRead(checkPermission() || false);
  }, [
    accessType,
    currentUserID,
    isMaster,
    isMember,
    docId,
    isPassowrdMatch,
    allowAnonymousEdit,
    anonymousPassword,
    parentAnonymousPassword,
  ]);

  return canRead;
}

export function useWritePermission({
  writeAuthType,
}: {
  writeAuthType: AUTH_TYPE_VALUE;
}): boolean {
  // fuego.auth()는 비반응형(synchronous)이므로, Recoil 상태 사용 (LayoutWrapper가 관리)
  const currentUserID = useRecoilValue(currentUserIDState);
  const currentUserAuthType = useRecoilValue(currentUserAuthTypeState);

  // ANONYMOUS 타입은 비로그인 포함 누구나 허용 (익명 글쓰기 기능)
  if (writeAuthType === AUTH_TYPE.ANONYMOUS) {
    return true;
  }

  // 로그인하지 않은 경우 — ANONYMOUS를 제외하고 항상 차단
  if (!currentUserID) {
    return false;
  }

  switch (writeAuthType) {
    case AUTH_TYPE.MASTER:
      return currentUserAuthType === AUTH_TYPE.MASTER;
    case AUTH_TYPE.MEMBER:
      return (
        currentUserAuthType === AUTH_TYPE.MASTER ||
        currentUserAuthType === AUTH_TYPE.MEMBER
      );
    case AUTH_TYPE.GUEST:
      // GUEST는 로그인된 모든 사용자에게 허용
      return true;
    default:
      return false;
  }
}

type permissionProps = {
  authorUid?: string;
  dependency?: any;
  accessType?: ACCESS_TYPE_VALUE | string;
  anonymous?: string;
  passAnonymous?: boolean;
  parentAuthorUid?: string | null;
};

//useWritePermission 으로 대체 가능할듯 함 - permissionType에 AUTH_TYPE.MASTER
export function useMasterPermission({ dependency }: permissionProps): boolean {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  // NOTE: 로그인 판정 축을 Recoil + authInitialized 로 통일.
  //  - 기존 `fuego.auth().currentUser?.uid` 동기 참조 제거 (onAuthStateChanged 첫 콜백 전 undefined 레이스)
  //  - 쿠키(`cookieStore.getUserToken()`) 의존 제거 (비반응성으로 stale 판정)
  //  - `authInitializedState` 가 true 로 확정되기 전에는 false 유지하되, 확정되면 재평가
  const authInitialized = useRecoilValue(authInitializedState);
  const currentUserID = useRecoilValue(currentUserIDState);
  const isMaster = useRecoilValue(isMasterState);

  useEffect(() => {
    if (authInitialized && currentUserID && isMaster) {
      setIsAuth(true);
    } else {
      setIsAuth(false);
    }
  }, [dependency, authInitialized, currentUserID, isMaster]);

  return isAuth;
}

// useWritePermission 으로 대체 가능할듯 함- permissionType에 AUTH_TYPE.MEMBER
export function useMemberPermission({ dependency }: permissionProps): boolean {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  // Recoil SSOT + authInitialized 게이팅
  const authInitialized = useRecoilValue(authInitializedState);
  const currentUserID = useRecoilValue(currentUserIDState);
  const isMaster = useRecoilValue(isMasterState);
  const isMember = useRecoilValue(isMemberState);

  useLayoutEffect(() => {
    if (authInitialized && currentUserID && (isMaster || isMember)) {
      setIsAuth(true);
    } else {
      setIsAuth(false);
    }
  }, [dependency, authInitialized, currentUserID, isMaster, isMember]);

  return isAuth;
}

// 로그인 되어있고, 글쓴이 본인일때만 수정 가능 true
export function useEditPermission({
  authorUid,
  dependency,
  passAnonymous,
}: permissionProps): boolean {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  //+ 마스터일때 수정 가능
  const currentUserID = useRecoilValue(currentUserIDState);
  const isMaster = useRecoilValue(isMasterState);

  useLayoutEffect(() => {
    const checkPermission = () => {
      if (currentUserID) {
        // 로그인 되어있는 경우
        if (isMaster) {
          // 마스터일 경우 항상 수정 가능
          return true;
        } else {
          // 글쓴이 본인일 경우 수정 가능
          return Boolean(authorUid === currentUserID);
        }
      }
      if (!currentUserID) {
        // 로그인되지 않았지만 익명 사용자 패스 조건을 만족하는 경우 수정 가능
        return Boolean(passAnonymous);
      }

      // 그 외의 경우 수정 불가능
      return false;
    };
    setIsAuth(checkPermission());
  }, [dependency, currentUserID, authorUid, passAnonymous, isMaster]);

  return isAuth;
}

// 컨트롤 버튼 ... 이 보이는가 여부
// '수정'가능한 권한과 다름! (삭제, 기타등등 기능이 포함될 수 있음)
export function useControlPermission({
  authorUid,
  anonymous,
  dependency,
}: permissionProps): boolean {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const cashAnonymousPassword = cookieStore.getAnonymousPassword();

  //로그인 되어있고, 글쓴이 본인이거나, master일 때 컨트롤 가능 true
  const currentUserID = useRecoilValue(currentUserIDState);
  const isMaster = useRecoilValue(isMasterState);
  const isOwner = (currentUserID && authorUid === currentUserID) || isMaster;

  useLayoutEffect(() => {
    const checkPermission = () => {
      if (currentUserID) {
        // 로그인 되어있는 경우
        return Boolean(isOwner);
      } else if (!currentUserID) {
        // 익명일 시 익명 사용자 패스 조건을 만족하는 경우 컨트롤 가능
        return Boolean(
          cashAnonymousPassword && anonymous === cashAnonymousPassword,
        );
      } else {
        // 그 외의 경우 컨트롤 불가능
        return false;
      }
    };

    setIsAuth(checkPermission());
  }, [dependency, currentUserID, authorUid, isMaster]);

  return isAuth;
}

export function usePasswordProtectedPermission({ accessType, docId }) {
  const [isProtected, setIsProtected] = useState<boolean>(false);
  //로그인 안되어 있어도 열람 가능

  useLayoutEffect(() => {
    if (accessType === ACCESS_TYPE.PASSWORD) {
      setIsProtected(true);
    } else {
      setIsProtected(false);
    }
  }, [accessType, docId]);

  return [isProtected, setIsProtected];
}

export function useAnonymousEditPermission({ authorUid, anonymousPassword }) {
  const [isAuth, setIsAuth] = useState<boolean>(false);
  //로그인 안되어있고, 글쓴이 본인이거나, master일 때 컨트롤 가능 true
  // 익명 비밀번호 쿠키는 기능상 필수 → 유지.
  // 로그인 판정축은 Recoil(currentUserIDState) 로 통일 (쿠키/Firebase 동기참조 제거).
  const cashAnonymousPassword = cookieStore.getAnonymousPassword();
  const currentUserID = useRecoilValue(currentUserIDState);

  useLayoutEffect(() => {
    if (
      !currentUserID &&
      cashAnonymousPassword &&
      anonymousPassword &&
      !authorUid
    ) {
      setIsAuth(true);
    } else {
      setIsAuth(false);
    }
  }, [
    currentUserID,
    authorUid,
    anonymousPassword,
    cashAnonymousPassword,
  ]);

  return isAuth;
}

/**
 * 특정 설정 ID에 대한 역할 기반 권한 확인
 * 관리자이거나, 멤버의 역할에 해당 settingId가 allowedSettings에 포함되어 있으면 true
 */
export function useRolePermission(settingId: string): boolean {
  const currentUserID = useRecoilValue(currentUserIDState);
  const isMaster = useRecoilValue(isMasterState);
  const isMember = useRecoilValue(isMemberState);

  const { data: userRoles } = useCollection<IMemberRole>(
    isMember && currentUserID ? MEMBER_ROLE_COLLECTION : null,
    {
      where: ['userIds', 'array-contains', currentUserID || ''],
      listen: true,
    },
  );

  const hasPermission = useMemo(() => {
    if (isMaster) return true;
    if (!isMember || !currentUserID || !userRoles) return false;
    return userRoles.some((role) =>
      role.allowedSettings?.includes(settingId),
    );
  }, [isMaster, isMember, currentUserID, userRoles, settingId]);

  return hasPermission;
}
